import type { ReactNode } from "react";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { StrategyCardSkeleton } from "./StrategyCardSkeleton";

/**
 * `NoDataState` (`prototype-extracted.md § 2.6`) — the dashed shell shared by the Error and
 * Never-pulled tab-level states (`components/seo-strategy.md § The screen`). No shared
 * component for it exists yet in this app, so it is rebuilt directly here, the same way
 * `StrategyBrowser.tsx`'s `ListEmptyState` rebuilds `EmptyActionState` for the list-level
 * shell rather than forking a primitive that doesn't exist.
 */
function StrategyNoDataShell({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-sm border border-border bg-card px-6 py-16 text-center">
      {icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-tint-medium">
          {icon}
        </div>
      )}
      <h4 className="text-lg font-semibold text-foreground">{title}</h4>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * The recommendations (or, as a fallback, the audit list) request errored. The retry is a
 * read, so it stays visible to a `viewer` — nothing here mutates anything
 * (`components/seo-strategy.md § The screen`, the Error row).
 */
export function StrategyErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <StrategyNoDataShell
      icon={<AlertCircle className="h-6 w-6 text-muted-foreground" />}
      title="Couldn't load strategies"
      body="Please try again."
      action={
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

/** No strategies, and no audit has ever completed, started or been queued for this ASIN. */
export function StrategyNeverPulledState({ onGenerate }: { onGenerate?: () => void } = {}) {
  return (
    <StrategyNoDataShell
      title="No strategy recommendations yet"
      body="Strategy recommendations are drawn from the Amazon AI score and keyword coverage. Please generate an Amazon AI score to get started."
      action={
        onGenerate ? (
          <Button variant="secondary" onClick={onGenerate}>
            {/* The design's EmptyActionState prefixes its action with a Plus
                (`signal-v2 DataStates.tsx`), as every other empty-state button here already does. */}
            <Plus className="mr-1 h-4 w-4" />
            Generate Amazon AI Score
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * No strategies yet, but an audit is `pending` or `processing` — recommendations depend on
 * it finishing. `DataLoadingState` (`prototype-extracted.md § 2.6`): a loading alert plus
 * one skeleton card, the same pairing `KeywordBankBuildState` uses for its own "building"
 * banner.
 */
export function StrategyBuildingState() {
  return (
    <div className="space-y-6">
      <Alert variant="info">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <AlertTitle>Building your strategy</AlertTitle>
        <AlertDescription>
          We&apos;re analyzing this ASIN&apos;s listing and keyword landscape. Recommendations
          will appear here once the first Amazon AI Score finishes — usually within a few
          hours of adding a product.
        </AlertDescription>
      </Alert>
      <StrategyCardSkeleton />
    </div>
  );
}
